import { Injectable } from '@angular/core'
import { Select, State, Action, StateContext, Selector, NgxsOnInit, Store, NgxsSimpleChange } from '@ngxs/store'
import { Observable } from 'rxjs';
import { UserState } from 'src/app/state-man/state/user.state';
import { User } from 'src/app/state-man/models/user.model';
import { AreaActions } from '../actions/area.actions';
import { Area } from '../models/area.model';
import { AreaService } from 'src/app/services/area.service';
import { StorageService } from 'src/app/services/storage.service';
import { stateContentChanged, sameRows } from './state-content-changed'

export class AreaStateModel {
    is_refreshing: boolean
    area: Area[]
    version?: string
}

@State<AreaStateModel>({
    name: 'area',
    defaults: {
        is_refreshing: true,
        area: []
    }
})
@Injectable()
export class AreaState implements NgxsOnInit {
    @Select(UserState.getUser) user$: Observable<User>

    constructor(        
        private areaService: AreaService,
        private store: Store,
        private storageService: StorageService
    ) {}

    async ngxsOnInit(ctx: StateContext<AreaStateModel>) {                
        this.user$.subscribe(async (user: User) => {            
            if (user.is_logged_in) {     
                this.storageService.events.subscribe(async (status: any) => {
                    if (this.storageService.is_initialized) {                        
                        await this.reloadStateFromStorage()                        
                    }
                })                           
            }
        }) 
    }

    async ngxsOnChanges(change: NgxsSimpleChange) {           
        if (this.storageService.is_initialized && stateContentChanged(change)) {   
            const state_model: AreaStateModel = change.currentValue         
            this.storageService.set('areas', JSON.stringify(state_model))  
        }    
    }

    async reloadStateFromStorage() {        
        const json: string = await this.storageService.get('areas');        
        if (json) {
            const state_model: AreaStateModel = JSON.parse(json)             
            if (state_model) {                
                this.store.dispatch(new AreaActions.Init(state_model));
                return
            } 
        }
        this.store.dispatch(new AreaActions.Refresh())
    }

    async reloadStateFromApi() : Promise<boolean> {    
        // Send the version only with rows to go with it.
        const held = this.store.selectSnapshot(AreaState.getState)
        const ret = await this.areaService.getAll(held.area?.length ? held.version ?? '' : '')
        if (ret?.rows) {
          // The service has already run buildMany over the response, so the
          // rows arriving here are built models. Building them a second time
          // allocated a duplicate object for every row and, where buildOne
          // rebuilds children, re-walked those too.
          this.store.dispatch(new AreaActions.Set(ret.rows, ret.version))
          return true
        }
        // Failed, or the list held is still current.
        return false
    }
  
    @Selector()
    static getState(state: AreaStateModel) {
        return state
    }

    @Selector()
    static getIsRefreshing(state: AreaStateModel) {
        return state.is_refreshing
    }

    @Selector()
    static getAll(state: AreaStateModel) {
        return state.area
    }

    @Action(AreaActions.Refresh)
    async refresh({patchState, getState}: StateContext<AreaStateModel>, {  }:AreaActions.Refresh) {
        patchState({is_refreshing: true})
        if (!await this.reloadStateFromApi()) {            
            patchState({is_refreshing: false, area: sameRows(getState().area)})
        }
    }
    
    @Action(AreaActions.Init)
    init({setState, getState}: StateContext<AreaStateModel>, { payload }:AreaActions.Init) {                  
        const state = getState()
        payload.is_refreshing = state.is_refreshing
        setState(payload)
        this.store.dispatch(new AreaActions.Refresh())
    }

    @Action(AreaActions.Set)
    set({setState}: StateContext<AreaStateModel>, { payload, version }:AreaActions.Set) {             
        setState({    
            is_refreshing: false,        
            area: payload,
            version
        })
    }
}
